import { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { Container } from "../Users/styles";
import { Button, Input, Select, TableColumnsType, Tooltip } from "antd";
import { sanitizeHtml } from "../../utils/helpers/sanitizeHtml";
import { CaretDownOutlined, CaretUpOutlined, SearchOutlined, CalendarOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { convertUtcToLocal } from "../../utils/helpers/convertUtcToLocal";
import { useAppDispatch, useAppSelector } from "../../utils/hooks/redux";
import { DefaultUserName, EmailLog } from "../../types/ActivityLog";
import { selectLogs } from "../../redux/features/logs/logsSlice";
import { getAllEmailLogs } from "../../redux/features/logs/logsAsyncThunk";
import { RequestResults } from "../../redux/common/RequestStatus";
import { ClaimButton, EmailBodyContainer, LogsDescription, LogsTable, ShowMoreButton } from "./styles";
import SystemPayRoutes from "../../utils/constants/Routes";
import { selectUserRole } from "../../redux/features/auth/selectors";
import { checkHasPermission } from "../../utils/helpers/checkHasPermission";
import { permissions } from "../../utils/constants/permissions";
import { fetchEstablishmentType } from "../../redux/features/establishmentType/establishmentAsyncThunk";
import { selectEstablishmentType } from "../../redux/features/establishmentType/establishmentTypeSlice";
import DatePickerModal from "../../components/DatePickerModal";
import {
  FilterTabWrapper,
  StatusFilters,
  FilterInputs,
  StyledSelect
} from "../Customers/styles";
import {
  EnvironmentOutlined,
  CloudOutlined,
  BankOutlined,
  CarOutlined,
  RocketOutlined,
  SmileOutlined,
  TrophyOutlined,
  ApartmentOutlined,
  HomeOutlined,
  RestOutlined,
  DollarCircleOutlined,
} from "@ant-design/icons";
dayjs.extend(isBetween);

const EmailLogs = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentLimit, setCurrentLimit] = useState<number>(50);
  const [searchField, setSearchField] = useState<string | undefined>();
  const [searchValue, setSearchValue] = useState<string | undefined>();
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string | undefined>();
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);
  const [dateField, setDateField] = useState<string | undefined>();
  const [fromDate, setFromDate] = useState<string | undefined>();
  const [toDate, setToDate] = useState<string | undefined>();
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const dispatch = useAppDispatch();
  const { emailLogs: { data: emailLogs, status, counts } } = useAppSelector(selectLogs);
  const { data: establishmentTypes, status: establishmentTypeStatus } = useAppSelector(selectEstablishmentType);
  const handleSearch = (value: string) => {
    setSearchValue(value);
    setCurrentPage(1);
  };

  const handleSearchFieldChange = (value: string | null) => {
    setSearchField(value || undefined);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => setCurrentPage(page);

  const handleLimitChange = (limit: number) => {
    setCurrentLimit(limit);
    setCurrentPage(1);
  };

  const addKeyProperty = (data: EmailLog[]) => {
    return data.map((item: EmailLog) => ({ ...item, key: item.id }));
  };

  // Function to toggle expanded rows
  const handleExpand = (key: string) => {
    setExpandedRowKeys((prevKeys) => {
      if (prevKeys.includes(key)) {
        return prevKeys.filter((k) => k !== key);
      } else {
        return [...prevKeys, key];
      }
    });
  };

  const handleClickOnClaimNumber = (record: EmailLog) => {
    if (record?.itemNumber) {
      window.open(`${SystemPayRoutes.foundReportBase}/${record?.itemNumber}?withDeleted=true`, '_blank');
    }
    else if (record?.trackingId) {
      window.open(`${SystemPayRoutes.reportBase}/${record?.trackingId}?withDeleted=true`, '_blank');
    }
  };

  const handleDateModalOk = (params: { dateField?: string; fromDate?: string; toDate?: string }) => {
    setDateField(params.dateField);
    setFromDate(params.fromDate);
    setToDate(params.toDate);
    setIsDateModalOpen(false);
    setCurrentPage(1);
  };

  const handleClearDateFilter = () => {
    setDateField(undefined);
    setFromDate(undefined);
    setToDate(undefined);
    setCurrentPage(1);
  };

  const columns: TableColumnsType<any> = useMemo(() => {
    return [
      { title: "#", dataIndex: "key", key: "key", render: (_, __, index) => `#${index + 1}` },
      { title: "Triggered By", dataIndex: "triggerBy", key: "triggerBy", render: (_, record: EmailLog) => <>{record?.triggerBy || DefaultUserName}</> },
      { title: "Claim Number", dataIndex: "trackingId", key: "trackingId", render: (_, record: EmailLog) => <ClaimButton type="link" onClick={() => handleClickOnClaimNumber(record)}>{record?.trackingId || record?.itemNumber || "-"}</ClaimButton> },
      { title: "Subject", dataIndex: "subject", key: "subject", render: (_, record: EmailLog) => <Tooltip title={record?.subject}><LogsDescription>{record?.subject}</LogsDescription></Tooltip> },
      { title: "To", dataIndex: "to", key: "to" },
      {
        title: "Sent On",
        dataIndex: "createdAt",
        key: "createdAt",
        render: (createdAt) => <div>{createdAt ? convertUtcToLocal(createdAt, "MM/DD/YYYY hh:mm A") : "-"}</div>,
      },
      { title: "Status", dataIndex: "status", key: "status", render: (_, record: EmailLog) => <>{record?.status || "-"}</> },
      {
        title: "Content",
        key: "content",
        dataIndex: "content",
        render: (_, record) => {
          const isExpanded = expandedRowKeys.includes(record.id);
          return (
            <ShowMoreButton
              type="text"
              icon={
                isExpanded ? (
                  <div>
                    Hide <CaretUpOutlined />
                  </div>
                ) : (
                  <div>
                    Show <CaretDownOutlined />
                  </div>
                )
              }
              onClick={() => handleExpand(record.id)}
            />
          );
        },
      },
    ];
  }, [expandedRowKeys, emailLogs, currentPage, currentLimit]);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearchValue(searchValue), 1000);
    return () => clearTimeout(handler);
  }, [searchValue]);

  useEffect(() => {
    if (establishmentTypeStatus === "idle") {
      dispatch(fetchEstablishmentType());
    }
  }, [dispatch, establishmentTypeStatus]);

  const establishmentIconPool = useMemo(
    () => [
      <EnvironmentOutlined style={{ fontSize: 16 }} />,
      <CloudOutlined style={{ fontSize: 16 }} />,
      <BankOutlined style={{ fontSize: 16 }} />,
      <CarOutlined style={{ fontSize: 16 }} />,
      <RocketOutlined style={{ fontSize: 16 }} />,
      <SmileOutlined style={{ fontSize: 16 }} />,
      <TrophyOutlined style={{ fontSize: 16 }} />,
      <ApartmentOutlined style={{ fontSize: 16 }} />,
      <HomeOutlined style={{ fontSize: 16 }} />,
      <RestOutlined style={{ fontSize: 16 }} />,
      <DollarCircleOutlined style={{ fontSize: 16 }} />,
    ],
    []
  );

  const establishmentTypeOptions = useMemo(() => {
    return establishmentTypes.map((establishmentType) => ({
      label: establishmentType.name,
      value: establishmentType.id,
      key: establishmentType.id,
      fullUrl: establishmentType.fullUrl,
    }));
  }, [establishmentTypes]);

  const establishmentSelectOptions = useMemo(() => {
    const mappedOptions = establishmentTypeOptions.map((filter: any) => {
      const randomIndex = Math.floor(Math.random() * establishmentIconPool.length);
      const icon = establishmentIconPool[randomIndex];
      return {
        label: (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {filter?.fullUrl ? (
              <img src={filter.fullUrl} alt={filter.label} style={{ width: 18, height: 18 }} />
            ) : (
              <div style={{ fontSize: 18 }}>{icon}</div>
            )}
            <span>{filter.label}</span>
          </div>
        ),
        value: filter.key,
      };
    });
    return [{ label: "All Establishments", value: null }, ...mappedOptions];
  }, [establishmentTypeOptions, establishmentIconPool]);

  const userRole = useAppSelector(selectUserRole);
  const hasPermission = useCallback(
    (permissionKey: string) => checkHasPermission(userRole, permissionKey),
    [userRole]
  );
  const isAccessPaymentLogs = useMemo(() => hasPermission(permissions.IsPaymentLogAllowed), [hasPermission]);

  const isDateRangeSelected = useMemo(() => !!(fromDate && toDate), [fromDate, toDate]);

  const formattedDateRange = useMemo(() => {
    return fromDate && toDate
      ? `${dayjs(fromDate).format("MM/DD/YYYY")} - ${dayjs(toDate).format("MM/DD/YYYY")}`
      : "Date Range";
  }, [fromDate, toDate]);

  useEffect(() => {
    dispatch(
      getAllEmailLogs({
        offset: (currentPage - 1) * currentLimit,
        limit: currentLimit,
        searchField,
        searchValue: debouncedSearchValue?.trim(),
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        dateField: fromDate && toDate ? "emailLog.createdAt" : "",
        keepPaymentLog: isAccessPaymentLogs,
        establishmentType: activeFilter
      })
    );
  }, [dispatch, currentPage, currentLimit, searchField, debouncedSearchValue, fromDate, toDate, isAccessPaymentLogs, activeFilter]);

  return (
    <Container>
      <FilterTabWrapper>
        <StatusFilters>
          <StyledSelect
            style={{ minWidth: 200 }}
            options={establishmentSelectOptions}
            value={activeFilter}
            onChange={(value) => setActiveFilter(value as string | null)}
            placeholder="Establishment"
            size="middle"
          />
        </StatusFilters>

        <FilterInputs>
          <StyledSelect
            allowClear
            value={searchField}
            options={[
              { value: "emailLog.to", label: "To Email" },
              { value: "emailLog.subject", label: "Subject" },
              { value: "emailLog.trackingId", label: "Claim Number" },
              { value: "emailLog.triggerBy", label: "Triggered By" },
            ]}
            onChange={(value) => handleSearchFieldChange(value as string | null)}
            placeholder="Filter by"
            size="middle"
          />
          <Input
            placeholder="Search"
            prefix={<SearchOutlined />}
            value={searchValue}
            onChange={(e) => handleSearch(e.target.value)}
            allowClear
            style={{ width: 200 }}
          />
          <Tooltip title={isDateRangeSelected ? formattedDateRange : "Select date range"}>
            <Button
              icon={<CalendarOutlined />}
              onClick={() => setIsDateModalOpen(true)}
              type={isDateRangeSelected ? "primary" : "default"}
              style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4, height: 32, minWidth: 32, padding: "0 8px" }}
            >
              {isDateRangeSelected && (
                <CloseCircleOutlined
                  style={{ marginLeft: 4 }}
                  onClick={(e) => { e.stopPropagation(); handleClearDateFilter(); }}
                />
              )}
            </Button>
          </Tooltip>
        </FilterInputs>
      </FilterTabWrapper>

      <LogsTable
        columns={columns}
        dataSource={addKeyProperty(emailLogs)}
        loading={status === RequestResults.pending}
        expandable={{
          expandedRowRender: (record: any) => (
            <div style={{ width: '100%', overflowX: 'auto' }}>
              <EmailBodyContainer
                dangerouslySetInnerHTML={{ __html: sanitizeHtml((record.body || "").replace(/\n/g, "<br/>")) }}
              />
            </div>
          ),
          rowExpandable: (record: any) => !!record.body,
          expandedRowKeys: expandedRowKeys,
          onExpand: (expanded, record: any) => handleExpand(record.id),
        }}
        pagination={{
          current: currentPage,
          total: counts,
          pageSize: currentLimit,
          showSizeChanger: true,
          onChange: handlePageChange,
          onShowSizeChange: (_, size) => handleLimitChange(size),
        }}
      />

      <DatePickerModal
        isModalOpen={isDateModalOpen}
        handleOk={handleDateModalOk}
        handleCancel={() => setIsDateModalOpen(false)}
        values={{ dateField, fromDate, toDate }}
      />
    </Container>
  );
};

export default EmailLogs;
